import React, { useState, useEffect } from "react";
import axiosInstance from "../../../axios";
import { Loading } from "../../Other/Loading";
import toast from "react-hot-toast";
import { Trash2, Plus, Sparkles, Check, DollarSign, Tag, Info, Percent, Eye, Edit2, X, ArrowLeft } from "lucide-react";
import "./CreateOfferPage.scss";

const CreateOfferPage = () => {
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // View state toggle
  const [showForm, setShowForm] = useState(false);

  // Form State
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  
  // Category State
  const [isCustomCategory, setIsCustomCategory] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  
  const [selectedCourses, setSelectedCourses] = useState([]); // Array of course IDs
  
  // Discount State
  const [discountType, setDiscountType] = useState("fixed"); // 'fixed' or 'percentage'
  const [discount, setDiscount] = useState("");
  
  const [searchTerm, setSearchTerm] = useState("");

  // Edit / View State
  const [editingOfferId, setEditingOfferId] = useState(null);
  const [viewingOffer, setViewingOffer] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [coursesRes, categoriesRes, offersRes] = await Promise.all([
        axiosInstance.get("/course"),
        axiosInstance.get("/categories"),
        axiosInstance.get("/offers"),
      ]);

      // Handle courses list
      if (coursesRes?.data?.data?.data) {
        // filter for published courses (Status = 3)
        const publishedCourses = coursesRes.data.data.data.filter(c => c.Status === 3);
        setCourses(publishedCourses);
      }

      // Handle categories
      if (categoriesRes?.data?.data) {
        setCategories(categoriesRes.data.data);
      }

      // Handle offers
      if (offersRes?.data?.data) {
        setOffers(offersRes.data.data);
      }
    } catch (error) {
      console.error("Error fetching initial data for Offers:", error);
      toast.error("Failed to load course or category data.");
    } finally {
      setLoading(false);
    }
  };

  const handleCourseToggle = (courseId) => {
    setSelectedCourses(prev => {
      if (prev.includes(courseId)) {
        return prev.filter(id => id !== courseId);
      } else {
        return [...prev, courseId];
      }
    });
  };

  // Calculate sum of selected courses
  const getSelectedCoursesTotal = () => {
    return selectedCourses.reduce((sum, id) => {
      const course = courses.find(c => c.ID === id);
      return sum + (course ? parseFloat(course.EnrollmentFee || 0) : 0);
    }, 0);
  };

  const originalTotal = getSelectedCoursesTotal();
  const discountInputVal = parseFloat(discount) || 0;
  
  // Calculate computed CAD discount value based on type
  const calculatedDiscount = discountType === "percentage"
    ? (originalTotal * discountInputVal) / 100
    : discountInputVal;

  const finalPrice = Math.max(0, originalTotal - calculatedDiscount);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("Please enter an Offer Title");
      return;
    }

    const categoryToSubmit = isCustomCategory ? customCategory.trim() : selectedCategory;
    if (!categoryToSubmit) {
      toast.error("Please specify a Category");
      return;
    }

    if (selectedCourses.length === 0) {
      toast.error("Please select at least one course for the bundle");
      return;
    }

    if (discountInputVal <= 0) {
      toast.error("Please enter a valid discount value");
      return;
    }

    if (discountType === "percentage" && discountInputVal >= 100) {
      toast.error("Percentage discount cannot be 100% or greater");
      return;
    }

    if (discountType === "fixed" && discountInputVal >= originalTotal) {
      toast.error("Discount cannot be greater than or equal to the total amount");
      return;
    }

    setSubmitting(true);
    try {
      // If creating custom category, post to category register endpoint first
      if (isCustomCategory) {
        const name = customCategory.trim();
        const exists = categories.some(cat => {
          const catName = cat.categoryName || cat.name;
          return catName && catName.toLowerCase() === name.toLowerCase();
        });
        if (!exists) {
          try {
            await axiosInstance.post("/v1/category", { name });
          } catch (catErr) {
            console.warn("Could not register custom category in global lists:", catErr);
          }
        }
      }

      const payload = {
        title,
        description,
        category: categoryToSubmit,
        courses: selectedCourses,
        totalAmount: originalTotal,
        discount: calculatedDiscount,
        discountedAmount: finalPrice,
        discountType,
        discountValue: discountInputVal
      };

      let res;
      if (editingOfferId) {
        res = await axiosInstance.put(`/offers/${editingOfferId}`, payload);
      } else {
        res = await axiosInstance.post("/offers", payload);
      }

      if (res?.data && !res.data.error) {
        toast.success(editingOfferId ? "Offer updated successfully!" : "Offer created successfully!");
        resetForm();
        fetchInitialData();
      } else {
        toast.error(res.data.message || "Failed to save offer");
      }
    } catch (error) {
      console.error("Error saving offer:", error);
      toast.error("An error occurred while saving the offer");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setSelectedCategory("");
    setCustomCategory("");
    setIsCustomCategory(false);
    setSelectedCourses([]);
    setDiscount("");
    setSearchTerm("");
    setDiscountType("fixed");
    setEditingOfferId(null);
    setShowForm(false);
  };

  const handleEditOffer = (offer) => {
    setTitle(offer.title);
    setDescription(offer.description || "");
    
    // Check if the offer category is in our predefined categories list
    const isPredefined = categories.some(
      (cat) => (cat.categoryName || cat.name || "").toLowerCase() === offer.category.toLowerCase()
    );
    
    if (isPredefined) {
      setIsCustomCategory(false);
      setSelectedCategory(offer.category);
      setCustomCategory("");
    } else {
      setIsCustomCategory(true);
      setSelectedCategory("");
      setCustomCategory(offer.category);
    }
    
    setSelectedCourses(offer.courses || []);
    setDiscountType(offer.discountType || "fixed");
    setDiscount(offer.discountValue || offer.discount);
    setEditingOfferId(offer.id);
    setShowForm(true);
    
    // Scroll form into view
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteOffer = async (id) => {
    if (!window.confirm("Are you sure you want to delete this offer?")) return;

    try {
      const res = await axiosInstance.delete(`/offers/${id}`);
      if (res?.data && !res.data.error) {
        toast.success("Offer deleted successfully");
        setOffers(prev => prev.filter(o => o.id !== id));
        if (editingOfferId === id) resetForm();
      } else {
        toast.error(res.data.message || "Failed to delete offer");
      }
    } catch (error) {
      console.error("Error deleting offer:", error);
      toast.error("An error occurred while deleting the offer");
    }
  };

  const filteredCourses = courses.filter(c => 
    c.CourseName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.Category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="offers-loader">
        <Loading />
        <p className="mt-2 text-slate-500">Loading courses and configurations...</p>
      </div>
    );
  }

  return (
    <div className="admin-offers-container">
      <div className="offers-header-banner">
        <div className="flex justify-between items-center w-full flex-wrap gap-4">
          <div>
            <div className="flex items-center space-x-2">
              <Sparkles className="text-yellow-400 w-8 h-8" />
              <h1 className="text-2xl font-bold text-white">Manage Course Offers</h1>
            </div>
            <p className="text-slate-300 text-sm mt-1">
              Combine multiple courses, apply bundle discounts, and target specific categories.
            </p>
          </div>
          {!showForm && (
            <button 
              className="btn-add-offer-top"
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
            >
              <Plus size={16} />
              <span>Add Offer</span>
            </button>
          )}
        </div>
      </div>

      <div className="offers-content-layout">
        {showForm ? (
          /* Create / Edit Offer Form Workspace */
          <div className="offer-form-card">
            <div className="flex items-center justify-between mb-4 border-b pb-2">
              <h2 className="section-title mb-0">
                {editingOfferId ? "Edit Combined Offer" : "Create Combined Offer"}
              </h2>
              <button className="btn-back-to-list" onClick={resetForm}>
                <ArrowLeft size={16} />
                <span>Back to list</span>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="offer-form">
              <div className="form-group">
                <label htmlFor="offer-title">Offer Title</label>
                <input
                  id="offer-title"
                  type="text"
                  placeholder="e.g., Ultimate AI & Web Development Pack"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="offer-desc">Description</label>
                <textarea
                  id="offer-desc"
                  rows="3"
                  placeholder="Describe the offer package details..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              {/* Target Category Selector with Custom Toggle */}
              <div className="form-group">
                <div className="flex justify-between items-center mb-1">
                  <label htmlFor="offer-category" className="mb-0">Target Category</label>
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:text-blue-800 font-bold bg-transparent border-none p-0 cursor-pointer"
                    onClick={() => {
                      setIsCustomCategory(!isCustomCategory);
                      setSelectedCategory("");
                      setCustomCategory("");
                    }}
                  >
                    {isCustomCategory ? "Select Predefined Category" : "+ Create New Category"}
                  </button>
                </div>
                
                {isCustomCategory ? (
                  <input
                    type="text"
                    placeholder="Enter custom category name..."
                    value={customCategory}
                    onChange={(e) => setCustomCategory(e.target.value)}
                    required
                  />
                ) : (
                  <div className="select-wrapper">
                    <select
                      id="offer-category"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      required
                    >
                      <option value="">-- Select Category --</option>
                      {categories.map((cat) => {
                        const catName = cat.categoryName || cat.name;
                        return (
                          <option key={cat.id || cat.ID || catName} value={catName}>
                            {catName}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}
              </div>

              <div className="form-row">
                {/* Discount Amount with Type Toggle */}
                <div className="form-group">
                  <div className="flex justify-between items-center mb-1">
                    <label className="mb-0">Discount Value</label>
                    <div className="discount-type-tabs flex space-x-1">
                      <button
                        type="button"
                        className={`text-[10px] px-2 py-0.5 rounded font-bold border ${discountType === "fixed" ? "bg-blue-600 text-white border-blue-600" : "bg-slate-100 text-slate-600 border-slate-200"}`}
                        onClick={() => {
                          setDiscountType("fixed");
                          setDiscount("");
                        }}
                      >
                        CAD $
                      </button>
                      <button
                        type="button"
                        className={`text-[10px] px-2 py-0.5 rounded font-bold border ${discountType === "percentage" ? "bg-blue-600 text-white border-blue-600" : "bg-slate-100 text-slate-600 border-slate-200"}`}
                        onClick={() => {
                          setDiscountType("percentage");
                          setDiscount("");
                        }}
                      >
                        %
                      </button>
                    </div>
                  </div>
                  
                  <div className="input-with-icon">
                    {discountType === "fixed" ? (
                      <DollarSign size={16} className="input-icon" />
                    ) : (
                      <Percent size={16} className="input-icon" />
                    )}
                    <input
                      type="number"
                      min="0"
                      max={discountType === "percentage" ? "99.9" : undefined}
                      step="0.01"
                      placeholder={discountType === "fixed" ? "50.00" : "20"}
                      value={discount}
                      onChange={(e) => setDiscount(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Selected Courses</label>
                  <div className="badge-value">
                    {selectedCourses.length} courses
                  </div>
                </div>
              </div>

              {/* Courses Selection Box */}
              <div className="courses-selector-section">
                <div className="courses-selector-header">
                  <label>Select Courses to Bundle</label>
                  <input
                    type="text"
                    placeholder="Filter courses..."
                    className="course-filter-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="courses-list-scroll">
                  {filteredCourses.length === 0 ? (
                    <p className="no-courses-placeholder">No courses found matching filter</p>
                  ) : (
                    filteredCourses.map((c) => {
                      const isChecked = selectedCourses.includes(c.ID);
                      return (
                        <div 
                          key={c.ID} 
                          className={`course-selection-row ${isChecked ? 'selected' : ''}`}
                          onClick={() => handleCourseToggle(c.ID)}
                        >
                          <div className="checkbox-box">
                            {isChecked && <Check size={14} className="text-white" />}
                          </div>
                          <div className="course-row-details">
                            <span className="course-row-title">{c.CourseName}</span>
                            <span className="course-row-meta">{c.Category} | CAD ${c.EnrollmentFee}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Summary Pricing Block */}
              <div className="pricing-summary-card">
                <div className="summary-pricing-row">
                  <span>Total Amount:</span>
                  <span className="line-through font-semibold text-slate-500">CAD ${originalTotal.toFixed(2)}</span>
                </div>
                <div className="summary-pricing-row">
                  <span>Bundle Discount:</span>
                  <span className="text-green-600 font-semibold">
                    - CAD ${calculatedDiscount.toFixed(2)} {discountType === "percentage" && `(${discountInputVal}%)`}
                  </span>
                </div>
                <div className="divider"></div>
                <div className="summary-pricing-row final">
                  <span>Final Package Price:</span>
                  <span className="final-price text-2xl font-bold">CAD ${finalPrice.toFixed(2)}</span>
                </div>
              </div>

              <div className="form-action-buttons">
                <button type="submit" className="btn-submit-offer" disabled={submitting}>
                  {submitting ? "Saving..." : (
                    <>
                      <Plus size={18} />
                      <span>{editingOfferId ? "Update Offer Bundle" : "Create Offer Bundle"}</span>
                    </>
                  )}
                </button>
                <button type="button" className="btn-cancel-edit" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        ) : (
          /* Default: Active Offers Table List Workspace (Full Width) */
          <div className="existing-offers-card overflow-x-auto">
            <h2 className="section-title">Active Offers</h2>
            {offers.length === 0 ? (
              <div className="no-offers-placeholder">
                <Info size={32} className="text-slate-400 mb-2" />
                <p>No active bundle offers created yet. Click "+ Add Offer" to create one.</p>
              </div>
            ) : (
              <div className="offers-table-wrap">
                <table className="offers-list-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Original Sum</th>
                      <th>Final Price</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offers.map((offer) => (
                      <tr key={offer.id}>
                        <td className="font-bold text-slate-800">{offer.title}</td>
                        <td>
                          <span className="table-category-badge">{offer.category}</span>
                        </td>
                        <td className="line-through text-slate-400 text-xs">${parseFloat(offer.totalAmount).toFixed(2)}</td>
                        <td className="font-extrabold text-blue-600">${parseFloat(offer.discountedAmount).toFixed(2)}</td>
                        <td>
                          <div className="table-actions">
                            <button 
                              className="action-btn view" 
                              title="View Details"
                              onClick={() => setViewingOffer(offer)}
                            >
                              <Eye size={14} />
                              <span>View</span>
                            </button>
                            <button 
                              className="action-btn edit" 
                              title="Edit Offer"
                              onClick={() => handleEditOffer(offer)}
                            >
                              <Edit2 size={14} />
                              <span>Edit</span>
                            </button>
                            <button 
                              className="action-btn delete" 
                              title="Delete Offer"
                              onClick={() => handleDeleteOffer(offer.id)}
                            >
                              <Trash2 size={14} />
                              <span>Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* View Offer Details Modal */}
      {viewingOffer && (
        <div className="offer-view-modal-overlay" onClick={() => setViewingOffer(null)}>
          <div className="offer-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Offer Details</h3>
              <button className="btn-close-modal" onClick={() => setViewingOffer(null)}>
                <X size={18} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="detail-item">
                <span className="label">Title</span>
                <span className="value title">{viewingOffer.title}</span>
              </div>

              {viewingOffer.description && (
                <div className="detail-item">
                  <span className="label">Description</span>
                  <span className="value text-slate-600">{viewingOffer.description}</span>
                </div>
              )}

              <div className="detail-row">
                <div className="detail-item">
                  <span className="label">Category</span>
                  <span className="value">
                    <span className="category-badge">{viewingOffer.category}</span>
                  </span>
                </div>
                <div className="detail-item">
                  <span className="label">Discount Config</span>
                  <span className="value text-green-600 font-bold">
                    {viewingOffer.discountType === "percentage" 
                      ? `${parseFloat(viewingOffer.discountValue).toFixed(0)}% Off`
                      : `$${parseFloat(viewingOffer.discount).toFixed(2)} Off (Fixed)`}
                  </span>
                </div>
              </div>

              <div className="detail-pricing-block">
                <div className="price-item">
                  <span>Original Sum:</span>
                  <span className="line-through">${parseFloat(viewingOffer.totalAmount).toFixed(2)}</span>
                </div>
                <div className="price-item text-green-600 font-bold">
                  <span>Total Discount:</span>
                  <span>-${parseFloat(viewingOffer.discount).toFixed(2)}</span>
                </div>
                <div className="divider"></div>
                <div className="price-item final">
                  <span>Final Bundle Price:</span>
                  <span className="text-blue-600">${parseFloat(viewingOffer.discountedAmount).toFixed(2)}</span>
                </div>
              </div>

              <div className="modal-courses-list">
                <span className="label block mb-2">Included Courses ({viewingOffer.coursesData?.length || 0}):</span>
                <div className="courses-list-track">
                  {viewingOffer.coursesData?.map((c) => (
                    <div key={c.ID} className="modal-course-card">
                      <span className="course-name">{c.CourseName}</span>
                      <span className="course-price">${parseFloat(c.enrollmentFee || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-close" onClick={() => setViewingOffer(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateOfferPage;
